import type { ConversationResponse } from '@acme/api/admin';
import { useUnit } from 'effector-react';
import { Alert, Spinner } from 'flowbite-react';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  $chatWindowApi,
  $conversationSections,
  $conversations,
  $error,
  $isLoading,
  $searchQuery,
  $selectedUserId,
  $selectedUserName,
  pageOpened,
  selectConversation,
  setSearchQuery,
  setSelectedUserId,
} from './model';
import { ChatPanel, ConversationList, EmptyState, Sidebar } from './ui';

export function ConversationsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    chatWindowApi,
    conversationSections,
    conversations,
    error,
    isLoading,
    searchQuery,
    selectedUserId,
    selectedUserName,
    onPageOpened,
    onSelectConversation,
    onSetSearchQuery,
    onSetSelectedUserId,
  } = useUnit({
    chatWindowApi: $chatWindowApi,
    conversationSections: $conversationSections,
    conversations: $conversations,
    error: $error,
    isLoading: $isLoading,
    searchQuery: $searchQuery,
    selectedUserId: $selectedUserId,
    selectedUserName: $selectedUserName,
    onPageOpened: pageOpened,
    onSelectConversation: selectConversation,
    onSetSearchQuery: setSearchQuery,
    onSetSelectedUserId: setSelectedUserId,
  });

  useEffect(() => {
    onPageOpened();
  }, [onPageOpened]);

  useEffect(() => {
    onSetSelectedUserId(searchParams.get('chat') ?? undefined);
  }, [onSetSelectedUserId, searchParams]);

  const handleSelectConversation = (conversation: ConversationResponse) => {
    onSelectConversation(conversation);
    const userId = conversation.userId;
    if (!userId) return;

    const next = new URLSearchParams(searchParams);
    next.set('chat', userId);
    setSearchParams(next);
  };

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <Alert color="failure">
          <span className="font-medium">Ошибка!</span> {error}
        </Alert>
      )}

      <div className="flex h-[calc(100vh-48px)] min-h-[720px] w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md">
        <Sidebar searchValue={searchQuery} onSearchChange={onSetSearchQuery}>
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner size="xl" />
            </div>
          ) : conversations.length === 0 ? (
            <EmptyState />
          ) : (
            <ConversationList
              sections={conversationSections}
              selectedUserId={selectedUserId}
              onSelect={handleSelectConversation}
            />
          )}
        </Sidebar>

        <ChatPanel
          selectedUserId={selectedUserId}
          selectedUserName={selectedUserName}
          chatWindowApi={chatWindowApi}
        />
      </div>
    </div>
  );
}
